import { Injectable } from '@angular/core'
import { combineLatest, Observable } from 'rxjs'
import { map, shareReplay } from 'rxjs/operators'
import { AuthenticationService } from 'src/app/_services/authentication.service'
import { StateService } from 'src/app/_services/state.service'
import { RequestsService } from 'src/app/_shared/services/requests.service'
import Swal from 'sweetalert2'
import {
  ILoaders,
  IService,
  IServiceState,
  IServiceProvider,
  IPackage,
  ServiceResponse,
  IDistrictPricing,
} from './interface'
import { Service } from './models'

declare var $: any

const initialState: IServiceState = {
  currentServiceId: undefined,
  currentProviderId: undefined,
  serviceList: [],
  services: [],
  serviceProviderFilter: '',
  serviceProviderPricing: null,
  loaders: {
    singleOrder: false,
    singleSoilTestOrder: false,
    createOrder: false,
    serviceUpdate: false,
    serviceProviderCreate: false,
    serviceProviderDelete: false,
    getServices: false,
    serviceDelete: false,
    serviceProviderPricing: false,
    serviceProviderPricingUpdate: false,
    serviceProviderPricingDuplicate: false,
  },
  errors: {},
  success: {},
}

@Injectable({
  providedIn: 'root',
})
export class ServiceStateService extends StateService<IServiceState> {
  services$: Observable<IService[]> = this.select((state) => {
    return state.services
  })

  loaders$: Observable<ILoaders> = this.select((state) => {
    return state.loaders
  })

  errors$: Observable<Partial<ILoaders>> = this.select((state) => {
    return state.errors
  })
  success$: Observable<Partial<ILoaders>> = this.select((state) => {
    return state.success
  })

  currentService$: Observable<IService> = this.select((state) => {
    if (state.currentServiceId === '') {
      return new Service()
    }
    return state.services.find((item) => item.id === state.currentServiceId)
  }).pipe(shareReplay(1))

  /*
  current service providers
  */
  currentProviderSearch$: Observable<string> = this.select((state) => {
    return state.serviceProviderFilter
  })
  /*
  filtered current service providers
  */
  filteredCurrentServiceProviders$: Observable<IServiceProvider[]> =
    combineLatest(this.currentService$, this.currentProviderSearch$).pipe(
      map(([service, search]) => {
        return serviceProviderfilter(
          service ? service.service_providers : [],
          search
        )
      }),
      shareReplay(1)
    )

  serviceProviderPricing$ = this.select((state) => state.serviceProviderPricing)
  constructor(
    private requestService: RequestsService,
    private authenticationService: AuthenticationService
  ) {
    super(initialState)
    const currentUser = this.authenticationService.getCurrentUserValue()
    const { country } = currentUser
    this.loadServices({ country })
  }

  getCurrentServiceProviders(service_id: string): IService {
    const service = this.state.services.find((val) => val.id === service_id)
    return service
  }

  loadServiceCategories() {
    this.requestService
      .dash_get('service-categories', null)
      .subscribe((res: any) => {
        this.setState({ serviceList: res.results })
      })
  }

  loadServices(params = {}) {
    const queryParams = new URLSearchParams(params).toString()
    this.setState({ loaders: { ...this.state.loaders, getServices: true } })
    this.requestService
      .dash_get(`services?${queryParams}`, params, true)
      .subscribe(
        (res: any) => {
          this.setState({
            services: res.data,
            loaders: { ...this.state.loaders, getServices: false },
          })
        },
        (error) => {
          this.setState({
            loaders: { ...this.state.loaders, getServices: false },
          })
          Swal.fire({
            title: 'Loading service failed,contact admin',
            type: 'error',
            timer: 2000,
          })
        }
      )
  }

  selectService(id: string) {
    this.setState({
      currentServiceId: id,
      serviceProviderFilter: '',
    })
  }

  selectProvider(id: string) {
    this.setState({ currentProviderId: id })
  }

  updateServiceProviderFilter(filter: string) {
    this.setState({
      serviceProviderFilter: filter,
    })
  }

  updateService(id: string, update: Partial<IService>) {
    this.setState({
      loaders: { ...this.state.loaders, serviceUpdate: true },
      errors: { ...this.state.errors, serviceUpdate: false },
    })
    this.requestService.dash_patch(`services/${id}`, update, true).subscribe(
      (res: ServiceResponse) => {
        let servicesCopy = this.getServiceCopy(res)
        this.setState({
          loaders: { ...this.state.loaders, serviceUpdate: false },
          services: servicesCopy,
          success: { ...this.state.success, serviceUpdate: true },
        })
        setTimeout(() => {
          this.setState({
            success: { ...this.state.success, serviceUpdate: false },
          })
        }, 0)
        $('#serviceModal').modal('hide')
        Swal.fire({
          title: 'Service updated successfully',
          type: 'success',
          timer: 2000,
        })
      },
      (err) => {
        this.setState({
          loaders: { ...this.state.loaders, serviceUpdate: false },
          errors: { ...this.state.errors, serviceUpdate: true },
        })
        setTimeout(() => {
          this.setState({
            errors: { ...this.state.errors, serviceUpdate: false },
          })
        }, 2000)
      }
    )
  }
  createService(data: Partial<IService>) {
    this.setState({
      loaders: { ...this.state.loaders, serviceUpdate: true },
      errors: {},
    })
    this.requestService.dash_post(`services`, data, true).subscribe(
      (res: ServiceResponse) => {
        const service = res.data
        this.setState({
          loaders: { ...this.state.loaders, serviceUpdate: false },
          services: [service, ...this.state.services],
          success: { ...this.state.success, serviceUpdate: true },
        })
        setTimeout(() => {
          this.setState({
            success: { ...this.state.success, serviceUpdate: false },
          })
        }, 0)
        $('#serviceModal').modal('hide')
        Swal.fire({
          title: 'Service created successfully',
          type: 'success',
          timer: 2000,
        })
      },
      (err) => {
        this.setState({
          loaders: { ...this.state.loaders, serviceUpdate: false },
          errors: { ...this.state.errors, serviceUpdate: true },
        })
      }
    )
  }

  deleteService(id: string) {
    this.setState({ loaders: { ...this.state.loaders, serviceDelete: true } })
    this.requestService
      .dash_delete(id || this.state.currentServiceId, `services`, true)
      .subscribe(
        (res) => {
          Swal.fire({
            title: 'Service deleted successfully',
            type: 'success',
            timer: 2000,
          })
          let servicesCopy = this.getServiceCopy(res, false)
          this.setState({
            loaders: { ...this.state.loaders, serviceDelete: false },
            services: servicesCopy,
          })
          $('#serviceModal').modal('hide')
        },
        (_err) => {
          this.setState({
            loaders: { ...this.state.loaders, serviceDelete: false },
          })
          Swal.fire({
            title: 'Deleting Service failed',
            type: 'error',
            text: 'An error occured, try again later',
            timer: 2000,
          })
        }
      )
  }

  addServiceProvider(data: Partial<IServiceProvider>) {
    this.setState({
      loaders: { ...this.state.loaders, serviceProviderCreate: true },
    })
    this.requestService
      .dash_post(
        `services/${this.state.currentServiceId}/service_provider`,
        data,
        true
      )
      .subscribe(
        (res: ServiceResponse) => {
          let servicesCopy = this.getServiceCopy(res)
          this.setState({
            loaders: { ...this.state.loaders, serviceProviderCreate: false },
            services: servicesCopy,
          })
          Swal.fire({
            title: 'Service Provider Created successfully',
            type: 'success',
            timer: 2000,
          })
          $('#serviceProviderModal').modal('hide')
        },
        (err) => {
          this.setState({
            loaders: { ...this.state.loaders, serviceProviderCreate: false },
          })
          Swal.fire({
            title: 'Saving Service Provider failed',
            type: 'error',
            text: 'An error occured, try again later',
            timer: 3000,
          })
        }
      )
  }
  updateServiceProvider(id: string, data: Partial<IServiceProvider>) {
    this.setState({
      loaders: { ...this.state.loaders, serviceProviderCreate: true },
    })
    this.requestService
      .dash_put(
        `services/${this.state.currentServiceId}/service_provider/${id}`,
        data,
        true
      )
      .subscribe(
        (res: ServiceResponse) => {
          let servicesCopy = this.getServiceCopy(res)
          this.setState({
            loaders: { ...this.state.loaders, serviceProviderCreate: false },
            services: servicesCopy,
          })
          Swal.fire({
            title: 'Service Provider updated successfully',
            type: 'success',
            timer: 2000,
          })
        },
        (err) => {
          this.setState({
            loaders: { ...this.state.loaders, serviceProviderCreate: false },
          })
          Swal.fire({
            title: 'Updating Service Provider failed',
            type: 'error',
            text: 'An error occured, try again later',
            timer: 2000,
          })
        }
      )
  }
  deleteServiceProvider(id: string) {
    this.setState({
      loaders: { ...this.state.loaders, serviceProviderDelete: true },
    })
    this.requestService
      .dash_delete(
        id,
        `services/${this.state.currentServiceId}/service_provider`,
        true
      )
      .subscribe(
        (res: ServiceResponse) => {
          let servicesCopy = this.getServiceCopy(res)
          this.setState({
            loaders: { ...this.state.loaders, serviceProviderDelete: false },
            services: servicesCopy,
          })
          Swal.fire({
            title: 'Service Provider deleted successfully',
            type: 'success',
            timer: 2000,
          })
        },
        (_err) => {
          this.setState({
            loaders: { ...this.state.loaders, serviceProviderDelete: false },
          })
          Swal.fire({
            title: 'Deleting Service Provider failed',
            type: 'error',
            text: 'An error occured, try again later',
            timer: 2000,
          })
        }
      )
  }

  getServiceProviderPricing(id: string) {
    this.setState({
      loaders: { ...this.state.loaders, serviceProviderPricing: true },
    })
    this.requestService
      .dash_get(
        `services/${this.state.currentServiceId}/service_provider/${id}/pricing`,
        {},
        true
      )
      .subscribe(
        (res: { data: IDistrictPricing[] }) => {
          const districtMap: { [x: string]: IDistrictPricing } = {}
          res.data.forEach(
            (district) => (districtMap[district.district] = district)
          )
          const updatedData = {
            ...this.state.serviceProviderPricing,
            [id]: districtMap,
          }
          this.setState({
            serviceProviderPricing: updatedData,
            loaders: { ...this.state.loaders, serviceProviderPricing: false },
          })
        },
        (error) => {
          this.setState({
            loaders: { ...this.state.loaders, serviceProviderPricing: false },
          })
          Swal.fire({
            title: 'Loading service failed,contact admin',
            type: 'error',
            timer: 2000,
          })
        }
      )
  }

  updateDistrictPricing(params: {
    serviceProviderId: any
    data: IDistrictPricing
  }) {
    const { serviceProviderId, data } = params
    this.setState({
      loaders: { ...this.state.loaders, serviceProviderPricingUpdate: true },
    })
    this.requestService
      .dash_put(
        `services/${this.state.currentServiceId}/service_provider/${serviceProviderId}/pricing`,
        data,
        true
      )
      .subscribe(
        (res: { data: IDistrictPricing }) => {
          const updatedData = {
            ...this.state.serviceProviderPricing,
            [serviceProviderId]: {
              ...this.state.serviceProviderPricing[serviceProviderId],
              [res.data.district]: res.data,
            },
          }
          this.setState({
            serviceProviderPricing: updatedData,
            loaders: {
              ...this.state.loaders,
              serviceProviderPricingUpdate: false,
            },
          })
        },
        (err) => {
          this.setState({
            loaders: {
              ...this.state.loaders,
              serviceProviderPricingUpdate: false,
            },
          })
        }
      )
  }

  duplicateDistrictPricing(params: {
    serviceProviderId: any
    data: IPackage[]
    districts: string[]
  }) {
    const { serviceProviderId, data, districts } = params
    const request = {
      districts,
      data,
    }
    this.setState({
      loaders: { ...this.state.loaders, serviceProviderPricingDuplicate: true },
    })
    this.requestService
      .dash_post(
        `services/${this.state.currentServiceId}/service_provider/${serviceProviderId}/pricing`,
        request,
        true
      )
      .subscribe(
        (res: { data: Partial<IDistrictPricing[]> }) => {
          const districtMap: { [x: string]: IDistrictPricing } = {}
          res.data.forEach(
            (district) => (districtMap[district.district] = district)
          )
          const updatedData = {
            ...this.state.serviceProviderPricing,
            [serviceProviderId]: {
              ...this.state.serviceProviderPricing[serviceProviderId],
              ...districtMap,
            },
          }
          this.setState({
            serviceProviderPricing: updatedData,
            loaders: {
              ...this.state.loaders,
              serviceProviderPricingDuplicate: false,
            },
          })
        },
        (err) => {
          this.setState({
            loaders: {
              ...this.state.loaders,
              serviceProviderPricingDuplicate: false,
            },
          })
        }
      )
  }

  public get serviceProviderPricing() {
    return this.state.serviceProviderPricing
  }

  private getServiceCopy(res: ServiceResponse, replace: boolean = true) {
    let servicesCopy = [...this.state.services]
    const updatedService = res.data
    const index = this.state.services.findIndex(
      (emp) => emp.id === updatedService.id
    )
    if (replace) {
      servicesCopy.splice(index, 1, updatedService)
    } else {
      servicesCopy.splice(index, 1)
    }
    return servicesCopy
  }
}

function serviceProviderfilter(
  array: IServiceProvider[],
  filter: string
): IServiceProvider[] {
  if (filter) {
    return array.filter((item) => {
      const filterValue = filter.toLowerCase()
      return (
        (item.email || '').toLowerCase().indexOf(filterValue) > -1 ||
        (item.name || '').toLowerCase().indexOf(filterValue) > -1
      )
    })
  }
  return array
}
